import {
    AgreementSaveModel,
    DataProcessorAgreementSaveModel,
} from "../../../../../processingActivity/sharingOfDataStep/agreementDialog/DataProcessorAgreementDialog.types";
import { useSelectableDocumentsForLegalEntitiesQuery, useSelectableDocumentsQuery } from "../../../../../processingActivity/hooks/useSelectableDocumentQuery";
import { ISystemSupplier } from "./SystemSupplier";
import { useState } from "react";
import { SystemModel, SystemSupplierViewModel } from "../../../../../system/systemEdit/SystemEdit.types";
import { getSystemEditQueryKey } from "../../../../../system/systemEdit/SystemEdit.hooks";
import { ajaxQueue } from "../../../../../common/api/ajaxQueue";
import { deleteHttp, post, put } from "../../../../../common/api/apiShared";
import { isBool } from "../../../../../common/booleanOperations";
import { usePlanContext } from "../../../../../auth/planProvider/PlanProvider";
import { LevelOfProtectionType } from "../../../../../legalEntity/addLegalEntityDialog/AddLegalEntityDialog.types";
import { EUToUSDataPrivacyFrameworkId } from "../../../../../legalBasis/TransferBasis.Helper";
import { SelectableAgreement } from "../../../../../processingActivity/ProcessingActivity.types.ts";
import { ContractOnTransfer, SelectableContract, toSelectableAgreement } from "../../../../../contract/contract/Contract.types.ts";
import { useOptimisticUpdate } from "../../../../../common/hooks/useOptimisticUpdate.ts";
import { useDotLegalQuery, useDotLegalQueryClient, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useSystemSupplierHooks(props: ISystemSupplier) {
    const queryClient = useDotLegalQueryClient();
    const { informationSecurityFeatures } = usePlanContext();
    const optimisticUpdate = useOptimisticUpdate();
    const [showDescription, setShowDescription] = useState(false);
    const [showSecondarySuppliers, setShowSecondarySuppliers] = useState(false);
    const [dataProcessorAgreementSaveModel, setDataProcessorAgreementSaveModel] = useState<DataProcessorAgreementSaveModel | undefined>(undefined);

    const legalEntityIdOnTransfer = props.supplier.legalEntityId;

    const contractsOnSupplierUrl = `/system/supplier/${props.supplier.id}/contracts`;
    const contractsOnSupplierQuery = useDotLegalQuery<ContractOnTransfer>(contractsOnSupplierUrl, {
        enabled: !!legalEntityIdOnTransfer,
    });
    const selectableContractsUrl = `/LegalEntity/${legalEntityIdOnTransfer}/selectableContracts`;
    const selectableContractsQuery = useDotLegalQuery<Array<SelectableContract>>(selectableContractsUrl, {
        enabled: !!legalEntityIdOnTransfer,
    });

    const queryKey = getSystemEditQueryKey(props.system.id);

    let documentsQuery = useSelectableDocumentsQuery(true, props.supplier.legalEntityId);

    let subProcessorsDocumentsQuery = useSelectableDocumentsForLegalEntitiesQuery(
        true,
        props.supplier.secondarySuppliers.filter((s) => s.dataProcessorId).map((s) => s.dataProcessorId!) ?? []
    );

    let documentsLoading = documentsQuery.isLoading;

    let dataProcessorAgreementDocs: Array<SelectableAgreement> = documentsQuery.dataProcessorDocuments.map((d) => {
        return {
            id: d.id,
            name: d.name,
            contractOrDocumentType: d.documentTypeName,
            status: d.status,
            type: "document",
        };
    });
    let transferImpactAssessmentDocs = documentsQuery.transferImpactAssessmentDocuments;

    let subProcessorsTIADocsLoading = subProcessorsDocumentsQuery.isLoading;
    let subProcessorsTIADocs = subProcessorsDocumentsQuery.transferImpactAssessmentDocuments;

    const removeAgreementMutation = useDotLegalMutation(removeAgreementAPI, {
        onSuccess: (data) => {
            const system = data.value();
            queryClient.setQueryData(queryKey, system);
        },
    });
    const addAgreementMutation = useDotLegalMutation(addAgreementAPI, {
        onSuccess: (data) => {
            const system = data.value();
            queryClient.setQueryData(queryKey, system);
        },
    });

    const updateSupplierMutation = useDotLegalMutation(updateSupplierAPI, {
        onSuccess: (data) => {
            const system = data.value();
            queryClient.setQueryData(queryKey, system);
        },
    });

    const updateSupplier = async (changeFunc: (supplier: SystemSupplierViewModel) => void) => {
        const temp = { ...props.system };
        let tempSupplier = temp.suppliers.find((x) => x.id === props.supplier.id)!;
        changeFunc(tempSupplier);

        queryClient.setQueryData(queryKey, temp);
        await ajaxQueue().addRequest(updateSupplierMutation.mutateAsync, {
            systemId: props.system.id,
            supplier: tempSupplier,
        });
    };

    const supplierUpdater = {
        onLegalEntityChange: (legalEntityId: string | null) => {
            updateSupplier((s) => {
                s.legalEntityId = legalEntityId ?? undefined;
                s.dataProcessorAgreementIds = [];
                s.transferImpactAssessmentId = undefined;
                s.hasTransferImpactAssessment = false;
                s.hasDataProcessorAgreement = false;

                let selectedLegalEntity = props.legalEntityData?.find((x) => x.id === legalEntityId);

                s.transferBasisId = selectedLegalEntity?.transferBasisId;
                if (selectedLegalEntity?.levelOfProtection === LevelOfProtectionType.DataPrivacyFramework) {
                    s.transferBasisId = EUToUSDataPrivacyFrameworkId;
                }
            });
        },
        onIsSupplierADataProcessorChange: (isDataProcessor: boolean) => {
            updateSupplier((s) => {
                s.isSupplierADataProcessor = isDataProcessor;

                if (!isDataProcessor) {
                    s.generateSharings = false;
                    s.transferBasisId = undefined;
                    s.transferImpactAssessmentId = undefined;
                    s.hasTransferImpactAssessment = false;
                } else {
                    s.generateSharings = informationSecurityFeatures.automaticRegistrationOfDataProcessorTransfers;
                }
            });
        },
        onGenerateSharingsChange: (value: boolean) => {
            updateSupplier((s) => {
                s.generateSharings = value;
            });
        },
        onTransferBasisChange: (value: string | null) => {
            updateSupplier((s) => {
                s.transferBasisId = value ?? undefined;
            });
        },
        onTransferImpactAssessmentChange: (tia: string | null) => {
            updateSupplier((s) => {
                if (tia) {
                    if (isBool(tia)) {
                        s.hasTransferImpactAssessment = tia.toLowerCase() === "true";
                        s.transferImpactAssessmentId = undefined;
                    } else {
                        s.transferImpactAssessmentId = tia;
                        s.hasTransferImpactAssessment = false;
                    }
                }
            });
        },
        onDataProcessorAgreementChange: (agreements: Array<string>) => {
            updateSupplier((s) => {
                if (agreements.length > 0) {
                    const boolAgreement = agreements.find((x) => isBool(x));
                    const isDocumentAdd = s.dataProcessorAgreementIds.length === 0 && agreements.length > 1;

                    if (boolAgreement && isDocumentAdd === false) {
                        let bool = boolAgreement.toLowerCase() === "true";
                        s.hasDataProcessorAgreement = bool;
                        s.dataProcessorAgreementIds = [];
                    } else {
                        s.dataProcessorAgreementIds = agreements;
                        s.hasDataProcessorAgreement = false;
                    }
                } else {
                    s.dataProcessorAgreementIds = [];
                }
            });
        },
        onServiceChange: (service: string) => {
            updateSupplier((s) => {
                s.service = service;
            });
        },
        onDescriptionChange: (description: string) => {
            updateSupplier((s) => {
                s.description = description;
            });
        },
        onAddAgreement: async (agreement: SelectableAgreement) => {
            if (agreement.type === "document") {
                const agreementId = agreement.id;
                const tempSystem = { ...props.system };
                const supplier = tempSystem.suppliers.find((d) => d.id === props.supplier.id)!;

                supplier.dataProcessorAgreementIds.push(agreementId);

                queryClient.setQueryData(queryKey, tempSystem);
                await ajaxQueue().addRequest(addAgreementMutation.mutateAsync, {
                    systemId: props.system.id,
                    supplierId: supplier.id,
                    saveModel: { agreementId: agreementId },
                });
            } else if (agreement.type === "contract") {
                const contractsOnTransfer = contractsOnSupplierQuery.data!;
                contractsOnTransfer.contractIds.push(agreement.id);
                await optimisticUpdate.putOnQueueAndSetQueryData(contractsOnTransfer, contractsOnSupplierUrl, addContractApi, {
                    supplierId: props.supplier.id,
                    contractId: agreement.id,
                });
            } else {
                throw new Error("Unknown agreement type " + agreement.type);
            }
        },
        onDeleteAgreement: async (agreement: SelectableAgreement) => {
            const agreementId = agreement.id;

            if (agreement.type === "document") {
                const tempSystem = { ...props.system };
                const supplier = tempSystem.suppliers.find((d) => d.id === props.supplier.id)!;

                supplier.dataProcessorAgreementIds = supplier.dataProcessorAgreementIds.filter((x) => x !== agreementId);

                queryClient.setQueryData(queryKey, tempSystem);
                await ajaxQueue().addRequest(removeAgreementMutation.mutateAsync, {
                    systemId: props.system.id,
                    supplierId: supplier.id,
                    saveModel: { agreementId: agreementId },
                });
            } else if (agreement.type === "contract") {
                const contractsOnTransfer = contractsOnSupplierQuery.data!;
                contractsOnTransfer.contractIds = contractsOnTransfer.contractIds.filter((x) => x !== agreementId);
                await optimisticUpdate.putOnQueueAndSetQueryData(contractsOnTransfer, contractsOnSupplierUrl, removeContractApi, {
                    supplierId: props.supplier.id,
                    contractId: agreementId,
                });
            } else {
                throw new Error("Unknown agreement type " + agreement.type);
            }
        },
        onHasAgreementChange: async (hasAgreement: boolean) => {
            const tempSystem = { ...props.system };
            const supplier = tempSystem.suppliers.find((d) => d.id === props.supplier.id)!;

            supplier.hasDataProcessorAgreement = hasAgreement;
            supplier.dataProcessorAgreementIds = [];

            queryClient.setQueryData(queryKey, tempSystem);
            await ajaxQueue().addRequest(addAgreementMutation.mutateAsync, {
                systemId: props.system.id,
                supplierId: supplier.id,
                saveModel: { hasAgreement: hasAgreement },
            });
        },
    };

    const addSecondarySupplierMutation = useDotLegalMutation(addSecondarySupplierAPI, {
        onSuccess: (data) => {
            const system = data.value();
            queryClient.setQueryData(queryKey, system);
        },
    });

    const addSecondarySupplier = async (isLink: boolean) => {
        await addSecondarySupplierMutation.mutateAsync(isLink);
    };

    const removeSecondarySupplierMutation = useDotLegalMutation(removeSecondarySupplierApi, {
        onSuccess: (data) => {
            const system = data.value();
            queryClient.setQueryData(queryKey, system);
        },
    });

    const removeSecondarySupplier = async (secondarySupplierId: string) => {
        const temp = { ...props.system };
        let tempSupplier = temp.suppliers.find((x) => x.id === props.supplier.id)!;
        tempSupplier.secondarySuppliers = tempSupplier.secondarySuppliers.filter((x) => x.id !== secondarySupplierId);

        queryClient.setQueryData(queryKey, temp);

        await removeSecondarySupplierMutation.mutateAsync({ secondarySupplierId: secondarySupplierId });
    };

    const selectableContracts: Array<SelectableAgreement> = selectableContractsQuery.data?.map((c) => toSelectableAgreement(c)) ?? [];

    const onLegalEntityOnTransferChange = () => {
        if (contractsOnSupplierQuery.data) {
            const newData = { ...contractsOnSupplierQuery.data };
            newData.contractIds = [];
            queryClient.setQueryData(contractsOnSupplierUrl, newData);
        }
    };

    return {
        agreementsLoading: documentsLoading || contractsOnSupplierQuery.isLoading || selectableContractsQuery.isLoading,
        selectableAgreements: [...dataProcessorAgreementDocs, ...selectableContracts].sort((a, b) => a.name.localeCompare(b.name)),
        transferImpactAssessmentDocs,
        showSecondarySuppliers,
        setShowSecondarySuppliers,
        dataProcessorAgreementSaveModel,
        setDataProcessorAgreementSaveModel,
        saveAgreement,
        refetchDocuments: documentsQuery.refetch,
        subProcessorsTIADocsLoading,
        subProcessorsTIADocs,
        supplierUpdate: supplierUpdater,
        addSecondarySupplier,
        removeSecondarySupplier,
        showDescription,
        setShowDescription,
        isAddingSecondarySupplier: addSecondarySupplierMutation.isLoading,
        selectedAgreements: [...(props.supplier.dataProcessorAgreementIds ?? []), ...(contractsOnSupplierQuery.data?.contractIds ?? [])],
        containsNonAccessibleContracts: contractsOnSupplierQuery.data?.containsNonAccessibleContacts ?? false,
        onLegalEntityOnTransferChange,
    };

    function saveAgreement(agreement: SelectableAgreement) {
        supplierUpdater.onAddAgreement(agreement);

        documentsQuery.refetch();
    }

    async function updateSupplierAPI(data: { systemId: string; supplier: SystemSupplierViewModel }) {
        return await put("/system/" + data.systemId + "/supplier/" + data.supplier.id, data.supplier);
    }

    async function addSecondarySupplierAPI(isLink: boolean) {
        return await post<SystemModel>(`/system/${props.system.id}/supplier/${props.supplier.id}/secondarySupplier`, isLink);
    }

    async function removeSecondarySupplierApi(data: { secondarySupplierId: string }) {
        return await deleteHttp(`/system/${props.system.id}/supplier/${props.supplier.id}/secondarySupplier/${data.secondarySupplierId}`);
    }

    async function addAgreementAPI(data: { systemId: string; supplierId: string; saveModel: AgreementSaveModel }) {
        return await put("/system/" + data.systemId + "/supplier/" + data.supplierId + "/agreement", data.saveModel);
    }

    async function removeAgreementAPI(data: { systemId: string; supplierId: string; saveModel: AgreementSaveModel }) {
        return await deleteHttp(`/system/${data.systemId}/supplier/${data.supplierId}/agreement?agreementId=${data.saveModel.agreementId}`);
    }

    async function addContractApi(data: { supplierId: string; contractId: string }) {
        return await post(`/system/supplier/${data.supplierId}/contracts/${data.contractId}`, null);
    }

    async function removeContractApi(data: { supplierId: string; contractId: string }) {
        return await deleteHttp(`/system/supplier/${data.supplierId}/contracts/${data.contractId}`);
    }
}
